import useValidation from '@hooks/useValidation';
import { createContext, PropsWithChildren, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { AnyObject, ObjectSchema } from 'yup';

import { EditableType, GetSensorOrSubSensorConfigurationWithShorthand } from '@/types/sensor/sensorInstallation.types';

import { SensorFormStepsByType, SensorFormValidationsByType } from './sensorEditing.constants';

export enum StepStateEnum {
  COMPLETED = 'COMPLETED',
  ACTIVE = 'ACTIVE',
  UPCOMING = 'UPCOMING',
}

export type SubStep<T extends EditableType> = {
  label: string;
  component: () => ReactNode;
  validationFields?: (keyof GetSensorOrSubSensorConfigurationWithShorthand<T>)[];
};

export type Step<T extends EditableType> = {
  label: string;
  component: () => ReactNode;
  canLoop?: boolean;
  validationFields?: (keyof GetSensorOrSubSensorConfigurationWithShorthand<T>)[];
  children?: SubStep<T>[];
};

type ProgressState = {
  stepIndex: number;
  subStepIndex: number | null;
};

type SensorEditingContextType<T extends EditableType> = {
  steps: Step<T>[];
  setSteps: (value: Step<T>[]) => void;

  progress: ProgressState;
  setProgress: (value: ProgressState) => void;

  getStepState: (index: number) => StepStateEnum;
  getSubStepState: (stepIndex: number, subIndex: number) => StepStateEnum;

  goNext: (skipSubLoop?: boolean) => void;
  goBack: () => void;
  goTo: (stepIndex: number, subStepIndex?: number) => void;

  currentStep: Step<T>;
  currentSubStep: SubStep<T> | null;

  validationSchema:
    | ObjectSchema<Partial<GetSensorOrSubSensorConfigurationWithShorthand<T>>, AnyObject, unknown, ''>
    | undefined;
};

const SensorEditingContext = createContext<SensorEditingContextType<EditableType> | null>(null);

type Props<T extends EditableType> = {
  type: T;
} & PropsWithChildren;

export default function SensorEditingProvider<T extends EditableType>({ children, type }: Readonly<Props<T>>) {
  const [steps, setSteps] = useState<Step<T>[]>(SensorFormStepsByType[type]);

  const [progress, setProgress] = useState<ProgressState>({
    stepIndex: 0,
    subStepIndex: null,
  });

  const getStepState = useCallback(
    (index: number): StepStateEnum => {
      if (index < progress.stepIndex) return StepStateEnum.COMPLETED;
      if (index > progress.stepIndex) return StepStateEnum.UPCOMING;
      return StepStateEnum.ACTIVE;
    },
    [progress.stepIndex],
  );

  const getSubStepState = useCallback(
    (stepIndex: number, subIndex: number): StepStateEnum => {
      if (stepIndex !== progress.stepIndex) {
        return stepIndex < progress.stepIndex ? StepStateEnum.COMPLETED : StepStateEnum.UPCOMING;
      }

      const current = progress.subStepIndex;

      if (current === null) {
        return subIndex === 0 ? StepStateEnum.ACTIVE : StepStateEnum.UPCOMING;
      }

      if (subIndex < current) return StepStateEnum.COMPLETED;
      if (subIndex === current) return StepStateEnum.ACTIVE;
      return StepStateEnum.UPCOMING;
    },
    [progress.stepIndex, progress.subStepIndex],
  );

  const goNext = useCallback(
    (skipSubLoop = true) => {
      const step = steps[progress.stepIndex];
      const subCount = step.children?.length ?? 0;

      if (subCount > 0 && (!step.canLoop || !skipSubLoop || progress.subStepIndex !== 0)) {
        const currentSubIndex = progress.subStepIndex ?? 0;
        if (currentSubIndex < subCount - 1) {
          setProgress({
            stepIndex: progress.stepIndex,
            subStepIndex: currentSubIndex + 1,
          });
          return;
        } else if (step.canLoop) {
          setProgress({
            stepIndex: progress.stepIndex,
            subStepIndex: 0,
          });
          return;
        }
      }
      const isLastStep = progress.stepIndex === steps.length - 1;
      if (!isLastStep) {
        const nextStep = steps[progress.stepIndex + 1];
        const nextHasSub = (nextStep.children?.length ?? 0) > 0;

        setProgress({
          stepIndex: progress.stepIndex + 1,
          subStepIndex: nextHasSub ? 0 : null,
        });
      }
    },
    [progress.stepIndex, progress.subStepIndex, steps],
  );

  const goBack = useCallback(() => {
    const isFirstStep = progress.stepIndex === 0;
    if (progress.subStepIndex !== null) {
      if (progress.subStepIndex > 0) {
        setProgress({
          stepIndex: progress.stepIndex,
          subStepIndex: progress.subStepIndex - 1,
        });
        return;
      }
    }
    if (!isFirstStep) {
      const prevStep = steps[progress.stepIndex - 1];

      if (prevStep.children) {
        setProgress({
          stepIndex: progress.stepIndex - 1,
          subStepIndex: prevStep.canLoop ? 0 : prevStep.children.length - 1,
        });
      } else {
        setProgress({
          stepIndex: progress.stepIndex - 1,
          subStepIndex: null,
        });
      }
    }
  }, [progress.stepIndex, progress.subStepIndex, steps]);

  const goTo = useCallback(
    (stepIndex: number, subStepIndex?: number) => {
      if (stepIndex < 0 || stepIndex >= steps.length) {
        return;
      }
      const step = steps[stepIndex];
      const hasSub = step.children !== undefined;
      setProgress({
        stepIndex: stepIndex,
        subStepIndex: hasSub ? (subStepIndex ?? 0) : null,
      });
    },
    [steps],
  );

  const currentStep = steps[progress.stepIndex];
  const currentSubStep =
    progress.subStepIndex === null ? null : (currentStep.children?.[progress.subStepIndex] ?? null);

  const validationSchema = useValidation(SensorFormValidationsByType[type]);

  const value = useMemo(
    () => ({
      steps,
      setSteps,
      progress,
      setProgress,
      getStepState,
      getSubStepState,
      goNext,
      goBack,
      goTo,
      currentStep,
      currentSubStep,
      validationSchema,
    }),
    [
      steps,
      progress,
      getStepState,
      getSubStepState,
      goNext,
      goBack,
      goTo,
      currentStep,
      currentSubStep,
      validationSchema,
    ],
  );

  return (
    <SensorEditingContext.Provider value={value as SensorEditingContextType<EditableType>}>
      {children}
    </SensorEditingContext.Provider>
  );
}

export function useSensorEditingContext<T extends EditableType>() {
  const context = useContext(SensorEditingContext);

  if (!context) {
    throw new Error('useSensorEditingContext must be used within a SensorEditingProvider');
  }

  return context as SensorEditingContextType<T>;
}
